/**
 * PlayerPreviewPanel - Shows GM what players currently see
 * Displays the FOCUS node content and choices in player-view style
 * Choices are clickable to navigate to that node
 */

import React from 'react';
import { ChevronDown, ChevronUp, Eye, Users, ArrowRight } from 'lucide-react';

const PlayerPreviewPanel = ({ 
  nodeTitle,
  nodeContent,
  isPaused,
  isExpanded,
  onToggleExpand,
  onChoiceClick  // NEW: callback when a choice is clicked
}) => {
  // Parse choices/links from content
  const parseChoices = (content) => {
    const choices = [];
    const linkRegex = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
    let match;
    while ((match = linkRegex.exec(content || '')) !== null) {
      const displayText = match[1];
      const target = match[2] || match[1];
      choices.push({ displayText, target });
    }
    return choices;
  };

  // Remove link markup from content for display
  const cleanContent = (content) => {
    if (!content) return '';
    // Remove [[link|target]] and [[link]] patterns
    return content
      .replace(/\[\[([^\]|]+)\|[^\]]+\]\]/g, '')
      .replace(/\[\[([^\]]+)\]\]/g, '')
      .trim();
  };

  const choices = parseChoices(nodeContent);
  const displayContent = cleanContent(nodeContent);

  if (!isExpanded) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg">
        <button
          onClick={onToggleExpand}
          className="w-full flex items-center justify-between p-3 hover:bg-slate-700 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-400" />
            <span className="font-medium text-sm text-white">Player Preview</span>
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between p-3 border-b border-slate-600 hover:bg-slate-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-purple-400" />
          <span className="font-medium text-sm text-white">Player Preview</span>
          <span className="text-xs text-slate-400">(what players see)</span>
        </div>
        <ChevronUp className="h-4 w-4 text-slate-400" />
      </button>

      {/* Paused Banner */}
      {isPaused && (
        <div className="bg-amber-900/80 px-3 py-2 text-center text-amber-200 text-xs">
          ⏸ Session is PAUSED - Players see this overlay
        </div>
      )}

      {/* Preview Content - Styled like player view */}
      <div className="p-4 bg-slate-900 text-white">
        {/* Node Title */}
        <div className="flex items-center gap-2 mb-3">
          <Eye className="h-4 w-4 text-amber-400" />
          <h3 className="text-lg font-bold text-amber-400">
            {nodeTitle || 'No node selected'}
          </h3>
        </div>

        {/* Node Content */}
        <div className="prose prose-invert prose-sm max-w-none mb-4">
          {displayContent ? (
            <p className="text-slate-300 whitespace-pre-wrap">{displayContent}</p>
          ) : (
            <p className="text-slate-500 italic">No content to display</p>
          )}
        </div>

        {/* Choices - Now clickable! */}
        {choices.length > 0 && (
          <div className="border-t border-slate-700 pt-3">
            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">CHOICES (tap to navigate):</p>
            <div className="space-y-2">
              {choices.map((choice, i) => (
                <button
                  key={i}
                  onClick={() => onChoiceClick && onChoiceClick(choice.target)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-left
                             text-slate-200 hover:bg-slate-700 hover:border-slate-500 
                             transition-colors flex items-center justify-between group"
                >
                  <span>
                    <span className="text-amber-400 mr-2">→</span>
                    {choice.displayText}
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-amber-400 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No content state */}
        {!nodeTitle && !nodeContent && (
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm">
              Select a node in Story Navigator to preview
            </p>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-2 bg-slate-850 border-t border-slate-700 text-xs text-slate-500">
        💡 Tap a choice to navigate • When you press PLAY, players see this content
      </div>
    </div>
  );
};

export default PlayerPreviewPanel;
